import { useEffect, useRef, useState } from "react";
import { renderPostArt, paletteForHostname, hashSeed } from "./postArtGen";

// Deterministic generated artwork for posts that have no share image.
// Draws once per seed at 2x for crispness; optional PNG download link so the
// user can attach the image when publishing the post on LinkedIn.
export default function PostArt({
    seedString,
    width,
    height,
    rounded = false,
    downloadName = null,
    style = {},
}) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        renderPostArt(canvas, seedString, {
            palette: paletteForHostname(window.location.hostname),
        });
    }, [seedString, width, height]);

    const download = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = downloadName || "post-image.png";
        a.click();
    };

    // Layout notes: spacing (margins/padding) from `style` goes on the WRAPPER, never on the
    // canvas — padding inside a border-radius:50% box turns the circle into an oblong.
    // flexShrink 0 keeps a flex row (the card header) from squeezing the fixed-size art.
    // Rounded thumbs stay inline; the full-size block is display:flex so following text
    // (e.g. "Step 1.") starts on its own line below the download link.
    return (
        <span
            style={{
                display: rounded ? "inline-flex" : "flex",
                flexDirection: "column",
                alignItems: rounded ? "center" : "flex-start",
                flexShrink: 0,
                ...style,
            }}
        >
            <canvas
                ref={canvasRef}
                width={width * 2}
                height={height * 2}
                style={{
                    width: width + "px",
                    height: height + "px",
                    borderRadius: rounded ? "50%" : "8px",
                    display: "block",
                }}
                aria-label="Generated post image"
                role="img"
            />
            {downloadName ? (
                <a
                    href="#download-post-image"
                    onClick={(e) => { e.preventDefault(); download(); }}
                    style={{ fontSize: "0.75rem", marginTop: "0.25rem", marginBottom: "0.5rem" }}
                >
                    Download image
                </a>
            ) : null}
        </span>
    );
}

// Share image when the source declared one and it actually loads; generated
// art otherwise. Scraped og/twitter URLs are third-party and can outlive the
// task row, so a load error flips to the art fallback instead of a broken
// image glyph.
export function PostImageOrArt({
    src,
    seedString,
    width,
    height,
    rounded = false,
    alt = "",
    downloadable = false,
    style = {},
}) {
    const [broken, setBroken] = useState(false);
    const usable = src && (src.startsWith("http://") || src.startsWith("https://"));

    if (!usable || broken) {
        return (
            <PostArt
                seedString={seedString}
                width={width}
                height={height}
                rounded={rounded}
                downloadName={downloadable ? "post-image-" + hashSeed(seedString).toString(16) + ".png" : null}
                style={style}
            />
        );
    }

    // Same layout rules as the art branch: fixed square + radius on the img itself,
    // never squeezed by the flex row, spacing carried by the caller's style.
    // Padding inside a border-radius:50% box renders an oblong, so padding keys
    // are structurally stripped from caller style on the rounded branch.
    const safeStyle = { ...style };
    if (rounded) {
        Object.keys(safeStyle).forEach((k) => {
            if (k.indexOf("padding") === 0) delete safeStyle[k];
        });
    }
    return (
        <img
            src={src}
            alt={alt}
            onError={() => setBroken(true)}
            style={
                rounded
                    ? { width: width + "px", height: height + "px", objectFit: "cover", borderRadius: "50%", flexShrink: 0, ...safeStyle }
                    : { maxWidth: width + "px", maxHeight: height + "px", borderRadius: "8px", display: "block", ...safeStyle }
            }
        />
    );
}
